import { defineComponent as _defineComponent } from 'vue'
import { renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, unref as _unref, createVNode as _createVNode, withModifiers as _withModifiers, vModelText as _vModelText, normalizeClass as _normalizeClass, withDirectives as _withDirectives, createCommentVNode as _createCommentVNode } from "vue"

const _hoisted_1 = { class: "select-none" }
const _hoisted_2 = ["aria-label", "onClick"]
const _hoisted_3 = ["placeholder", "aria-expanded"]
const _hoisted_4 = ["aria-label"]
const _hoisted_5 = ["aria-selected", "onMousedown", "onMouseenter"]
const _hoisted_6 = {
  key: 0,
  class: "text-xs text-muted-foreground"
}

import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { X } from 'lucide-vue-next'

interface SuggestionItem {
  value: string
  label: string
  isCustom?: boolean
}


export default /*@__PURE__*/_defineComponent({
  __name: 'TagAutocomplete',
  props: {
    modelValue: { default: () => [] },
    options: { default: () => [] },
    placeholder: { default: 'Start typing to add software…' },
    addMorePlaceholder: { default: '+ Add another…' },
    maxSuggestions: { default: 8 },
    dropdownPlacement: { default: 'auto' },
    clearable: { type: Boolean, default: false },
    clearLabel: { default: 'Clear all' }
  },
  emits: ["update:modelValue"],
  setup(__props: any, { emit: __emit }) {

const props = __props

const emit = __emit

const sanitizeValue = (value: string): string => value.replace(/\s+/g, ' ').trim()
const toKey = (value: string): string => sanitizeValue(value).toLowerCase()

const wrapperRef = ref<HTMLDivElement | null>(null)
const query = ref('')
const isFocused = ref(false)
const activeIndex = ref(-1)
const inputRef = ref<HTMLInputElement | null>(null)
const listId = `tag-autocomplete-${Math.random().toString(36).slice(2, 8)}`
const dropdownDirection = ref<'up' | 'down'>('down')

const selectedTags = computed(() => props.modelValue ?? [])
const normalizedSelectedKeys = computed(() => {
  const set = new Set<string>()
  for (const tag of selectedTags.value) {
    const key = toKey(tag)
    if (key) set.add(key)
  }
  return set
})

const optionKeys = computed(() => {
  const set = new Set<string>()
  for (const option of props.options ?? []) {
    const key = toKey(option)
    if (key) set.add(key)
  }
  return set
})

const availableOptions = computed(() => {
  const result: string[] = []
  for (const option of props.options ?? []) {
    if (!normalizedSelectedKeys.value.has(toKey(option))) {
      result.push(option)
    }
  }
  return result
})

const inputPlaceholder = computed(() => {
  if (selectedTags.value.length === 0) return props.placeholder
  return props.addMorePlaceholder || props.placeholder
})

const inputFlexClasses = computed(() => {
  if (selectedTags.value.length === 0) {
    return 'flex-1 min-w-[110px]'
  }
  return 'mt-2 flex-[1_0_100%] min-w-full'
})

const showClear = computed(() => props.clearable && selectedTags.value.length > 0)
const clearLabel = computed(() => props.clearLabel || 'Clear all')

const suggestions = computed<SuggestionItem[]>(() => {
  const sanitizedQuery = sanitizeValue(query.value)
  const normalizedQuery = toKey(sanitizedQuery)
  const matches =
    normalizedQuery.length === 0
      ? availableOptions.value
      : availableOptions.value.filter((option) => toKey(option).includes(normalizedQuery))

  const limited = matches.slice(0, props.maxSuggestions)
  const items: SuggestionItem[] = limited.map((option) => ({
    value: option,
    label: option,
    isCustom: false,
  }))

  if (
    sanitizedQuery &&
    normalizedQuery &&
    !normalizedSelectedKeys.value.has(normalizedQuery) &&
    !optionKeys.value.has(normalizedQuery)
  ) {
    items.unshift({
      value: sanitizedQuery,
      label: `Add "${sanitizedQuery}"`,
      isCustom: true,
    })
  }

  return items
})

const shouldShowSuggestions = computed(() => isFocused.value && suggestions.value.length > 0)

watch(
  () => suggestions.value.length,
  () => {
    if (suggestions.value.length === 0) {
      activeIndex.value = -1
    } else if (activeIndex.value >= suggestions.value.length) {
      activeIndex.value = suggestions.value.length - 1
    }
  }
)

watch(
  () => shouldShowSuggestions.value,
  (visible) => {
    if (!visible) {
      activeIndex.value = -1
    } else if (activeIndex.value === -1 && suggestions.value.length > 0) {
      activeIndex.value = 0
      decideDropdownDirection()
    }
  }
)

const focusInput = () => {
  inputRef.value?.focus()
}

const handleFocus = () => {
  isFocused.value = true
  decideDropdownDirection()
}

const handleBlur = () => {
  window.setTimeout(() => {
    isFocused.value = false
  }, 100)
}

const handleKeydown = (event: KeyboardEvent) => {
  switch (event.key) {
    case 'ArrowDown': {
      event.preventDefault()
      if (!shouldShowSuggestions.value) {
        isFocused.value = true
        return
      }
      const nextIndex = activeIndex.value + 1
      activeIndex.value = nextIndex >= suggestions.value.length ? 0 : nextIndex
      break
    }
    case 'ArrowUp': {
      event.preventDefault()
      if (!shouldShowSuggestions.value) {
        isFocused.value = true
        return
      }
      const prevIndex = activeIndex.value - 1
      activeIndex.value = prevIndex < 0 ? suggestions.value.length - 1 : prevIndex
      break
    }
    case 'Enter': {
      event.preventDefault()
      if (shouldShowSuggestions.value && activeIndex.value >= 0) {
        selectSuggestion(suggestions.value[activeIndex.value])
      } else {
        addTag(query.value)
      }
      break
    }
    case 'Tab': {
      if (shouldShowSuggestions.value && activeIndex.value >= 0) {
        event.preventDefault()
        selectSuggestion(suggestions.value[activeIndex.value])
      } else {
        const sanitized = sanitizeValue(query.value)
        if (sanitized) {
          event.preventDefault()
          addTag(sanitized)
        }
      }
      break
    }
    case ',': {
      const sanitized = sanitizeValue(query.value)
      if (sanitized) {
        event.preventDefault()
        addTag(sanitized)
      }
      break
    }
    case 'Backspace': {
      if (!query.value && selectedTags.value.length > 0) {
        event.preventDefault()
        removeTag(selectedTags.value[selectedTags.value.length - 1])
      }
      break
    }
    case 'Escape': {
      if (shouldShowSuggestions.value) {
        event.preventDefault()
        isFocused.value = false
        activeIndex.value = -1
      }
      break
    }
    default:
      break
  }
}

const selectSuggestion = (item: SuggestionItem) => {
  addTag(item.value)
}

const addTag = (value: string) => {
  const sanitized = sanitizeValue(value)
  const key = toKey(sanitized)
  if (!sanitized || !key || normalizedSelectedKeys.value.has(key)) {
    query.value = ''
    return
  }

  emit('update:modelValue', [...selectedTags.value, sanitized])
  query.value = ''
}

const removeTag = (value: string) => {
  emit(
    'update:modelValue',
    selectedTags.value.filter((tag) => tag !== value)
  )
}

const clearAll = () => {
  if (!showClear.value) return
  emit('update:modelValue', [])
  query.value = ''
  inputRef.value?.focus()
}
const decideDropdownDirection = () => {
  if (props.dropdownPlacement === 'up' || props.dropdownPlacement === 'down') {
    dropdownDirection.value = props.dropdownPlacement
    return
  }

  const wrapper = wrapperRef.value
  if (!wrapper) {
    dropdownDirection.value = 'down'
    return
  }

  const rect = wrapper.getBoundingClientRect()
  const spaceBelow = window.innerHeight - rect.bottom
  dropdownDirection.value = spaceBelow < 220 ? 'up' : 'down'
}

const handleViewportChange = () => {
  if (isFocused.value) {
    decideDropdownDirection()
  }
}

onMounted(() => {
  window.addEventListener('resize', handleViewportChange, { passive: true })
  window.addEventListener('scroll', handleViewportChange, { passive: true })
  decideDropdownDirection()
})

onBeforeUnmount(() => {
  window.removeEventListener('resize', handleViewportChange)
  window.removeEventListener('scroll', handleViewportChange)
})

watch(
  () => props.dropdownPlacement,
  () => decideDropdownDirection()
)

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    ref_key: "wrapperRef",
    ref: wrapperRef,
    class: "relative"
  }, [
    _createElementVNode("div", {
      class: _normalizeClass([
        'mt-1 flex min-h-[2.9rem] w-full cursor-text flex-wrap items-center gap-1 rounded-xl border border-border/70 bg-card px-3 py-2 text-sm shadow-sm transition focus-within:border-brand/60 focus-within:shadow-[0_0_0_3px_rgba(16,103,255,0.12)] focus-within:ring-0 hover:border-border',
        showClear.value ? 'pr-16' : '',
      ]),
      onClick: focusInput
    }, [
      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(selectedTags.value, (tag) => {
        return (_openBlock(), _createElementBlock("span", {
          key: tag,
          class: "inline-flex items-center gap-1 rounded-full border border-brand/25 bg-brand/10 px-2.5 py-1 text-xs font-medium text-brand"
        }, [
          _createElementVNode("span", _hoisted_1, _toDisplayString(tag), 1),
          _createElementVNode("button", {
            type: "button",
            class: "rounded-full p-0.5 text-brand transition hover:bg-brand/20 hover:text-brand/80",
            "aria-label": `Remove ${tag}`,
            onClick: _withModifiers(($event: any) => (removeTag(tag)), ["stop"])
          }, [
            _createVNode(_unref(X), { class: "h-3.5 w-3.5" })
          ], 8, _hoisted_2)
        ]))
      }), 128)),
      _withDirectives(_createElementVNode("input", {
        ref_key: "inputRef",
        ref: inputRef,
        "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((query).value = $event)),
        placeholder: inputPlaceholder.value,
        "aria-controls": listId,
        "aria-expanded": shouldShowSuggestions.value,
        "aria-autocomplete": "list",
        autocomplete: "off",
        class: _normalizeClass([
          'border-none bg-transparent px-1 py-1.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus-visible:outline-none focus:ring-0 focus-visible:ring-0',
          inputFlexClasses.value,
        ]),
        role: "combobox",
        onFocus: handleFocus,
        onBlur: handleBlur,
        onKeydown: handleKeydown
      }, null, 42, _hoisted_3), [
        [_vModelText, query.value]
      ])
    ], 2),
    (showClear.value)
      ? (_openBlock(), _createElementBlock("button", {
          key: 0,
          type: "button",
          class: "absolute right-2 top-1/2 -translate-y-1/2 rounded-md border border-border/60 bg-background px-2 py-1 text-xs font-medium text-muted-foreground transition hover:border-border hover:text-foreground",
          "aria-label": `${clearLabel.value} software`,
          onClick: _withModifiers(clearAll, ["stop"])
        }, _toDisplayString(clearLabel.value), 9, _hoisted_4))
      : _createCommentVNode("", true),
    (shouldShowSuggestions.value)
      ? (_openBlock(), _createElementBlock("ul", {
          key: 1,
          id: listId,
          class: _normalizeClass([
        'absolute z-20 max-h-48 w-full overflow-y-auto rounded-lg border border-border/70 bg-card py-1 text-sm shadow-xl ring-1 ring-border/50',
        dropdownDirection.value === 'up' ? 'bottom-full left-0 mb-2' : 'top-full left-0 mt-2',
      ]),
          role: "listbox"
        }, [
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(suggestions.value, (item, index) => {
            return (_openBlock(), _createElementBlock("li", {
              key: item.value + index,
              role: "option",
              "aria-selected": index === activeIndex.value,
              class: _normalizeClass([
          'flex cursor-pointer items-center justify-between px-3 py-2 transition',
          index === activeIndex.value ? 'bg-muted text-foreground' : 'text-foreground hover:bg-muted',
        ]),
              onMousedown: _withModifiers(($event: any) => (selectSuggestion(item)), ["prevent"]),
              onMouseenter: ($event: any) => (activeIndex.value = index)
            }, [
              _createElementVNode("span", null, _toDisplayString(item.label), 1),
              (item.isCustom)
                ? (_openBlock(), _createElementBlock("span", _hoisted_6, "Custom"))
                : _createCommentVNode("", true)
            ], 42, _hoisted_5))
          }), 128))
        ], 2))
      : _createCommentVNode("", true)
  ], 512))
}
}

})